import React, {useState } from 'react';
import { Modal, Upload, Button, Spin, Divider } from 'antd';
import PropTypes from 'prop-types';
import { getPreSignedUrl } from '../../services/authApi/userApi';
import axios from 'axios';
import { toastNotify } from '../../utils/toastNotify';
import { getBase64 } from '../../utils/convertImageToBase64';

const RegDModal = ({ modalOpen, closeModal, setUploadedDoc }) => {
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState([]);
  const [isDoneButtonDisabled, setIsDoneButtonDisabled] = useState(true);
  const [fileError, setFileError] = useState('');
  const [numSelectedFiles, setNumSelectedFiles] = useState(0);
  const [isUploadButtonDisabled, setIsUploadButtonDisabled] = useState(false);

  const handleUpload = (info) => {
    info.onSuccess('ok');
  };

  const validateFiles = (files) => {
    const allowedFileTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxFileSizeKB = 100 * 1024; // 100MB in KB
    const maxFileNameLength = 100;

    const fileNames = new Set(); // To store unique file names

    if (files.length > 5) {
      return 'You can select a maximum of 5 files.';
    }

    for (const file of files) {
      const fileSizeKB = file.size / 1024;
      const isSizeValid = fileSizeKB > 1 && fileSizeKB <= maxFileSizeKB;
      const isTypeValid = allowedFileTypes.includes(file.type);
      const isNameValid = file.name.length <= maxFileNameLength;

      if (!isTypeValid) {
        return 'Only PDF, JPG, and PNG files are allowed.';
      } else if (!isSizeValid) {
        return 'File size should be between 1KB and 100MB.';
      } else if (!isNameValid) {
        return 'File name should not exceed 100 characters.';
      } else if (fileNames.has(file.name)) {
        return 'File names must be unique.';
      }

      fileNames.add(file.name);
    }
    return '';
  };

  const handleFileChange = (info) => {
    const updatedSelectedFiles = info.fileList;

    const validationError = validateFiles(updatedSelectedFiles);

    if (validationError) {
      setFileError(validationError);
      setIsDoneButtonDisabled(true);
    } else {
      setFileError('');
      setNumSelectedFiles(updatedSelectedFiles.length);
      setIsDoneButtonDisabled(false);
      setIsUploadButtonDisabled(false);
    }
    if (updatedSelectedFiles.length === 5) {
      setIsUploadButtonDisabled(true);
    }
    setSelectedFile(updatedSelectedFiles);
  };

  const handleRemoveUploaded = (file) => {
    setSelectedFile(selectedFile.filter(val => val.uid !== file.uid))
    setNumSelectedFiles(numSelectedFiles - 1);
    setFileError('');
  }

  const onSubmitDocument = async () => {
    setLoading(true);
    if (selectedFile.length === 0) {
      setLoading(false);
      return;
    }
    try {
      const uploadPromises = selectedFile.map(async (file) => {
        const fileType = file.name.split('.').pop().toLowerCase();
        const fileName = file.name;
        const displayView = 'Front';
        const imgBase64 = await getBase64(file.originFileObj);
        // Get pre-signed URL from the server
        const resp = await getPreSignedUrl(fileType, false, displayView, fileName);
        const preSignedUrl = resp?.data?.url;
        const storagePath = resp?.data?.storagePath;

        if (preSignedUrl) {
          const config = {
            headers: {
              'content-type': fileType,
              'Access-Control-Allow-Origin': '*',
            },
          };

          const formData = new FormData();
          formData.append('file', imgBase64);

          // Upload the file to S3 using the pre-signed URL
          await axios.put(preSignedUrl, file.originFileObj, config);

          // Notify parent component that the document was uploaded
          return storagePath;
        }
      });


      const uploadedFiles = await Promise.all(uploadPromises);
      // Check if all files were successfully uploaded
      if (uploadedFiles.every((file) => !!file)) {
        // Notify parent component that all documents were uploaded
        setUploadedDoc(uploadedFiles);
      } else {
        toastNotify('error', 'Some files failed to upload. Please try again.');
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
      closeModal();
    }
  }

  return (
    <Modal
      open={modalOpen}
      onCancel={() => closeModal('1')}
      footer={null}
      width={1000}
    >
      <span>
        <p className='accredited-text'><b>ACCREDITED INVESTOR VERIFICATION</b></p>
        <Divider className='accredited-divider' />
        <p className='accredited-text'>Please upload the following document(s) for <b>one</b> of the four methods to complete the verification:</p>
        <ul className='document-list'>
          <li>
            <b>Income:</b> W2’s, 1099’s or tax returns for the past <b>two</b> years to show your individual income exceeds $200,000 (or joint income exceeds $300,000) for each year.
          </li>
          <li>
            <b>Assets:</b> provide recent account statement(s) or third-party appraisals (must be dated within the last 3 months) that show the value of your assets exceeds $1,000,000 excluding your primary residence.
          </li>
          <li>
            <b>Professional Letter</b> (must be dated within the last 3 months): provide an official written communication from a licensed CPA, attorney, investment advisor or register broker-dealer, stating that the professional services provider has a reasonable belief that you are an Accredited investor.
          </li>
          <li>
            <b>Series 7, 65 or 82 licenses:</b> provide license indicating that holder is in good standing.
          </li>
        </ul>
        <p className='accredited-text'><b>Entities</b> can be accredited using one of two methods:</p>
        <ul className='document-list'>
          <li>
            The entity holds at least $5 million in qualified assets: provide account statements (must be dated within the last 3 months).
          </li>
          <li>
            Each equity owner of the entity is personally accredited: provide an operating agreement or share holder roster listing the entity’s owners, as well as evidence of the owners’ accreditation.
          </li>
        </ul>
        <p>
          <b>Note:</b> you can select multiple files, if needed using PDF, JPG and PNG file format (each document cannot be larger than 100 MB). You can only upload up to <b>5 documents.</b> Filename must not exceed <b>100 characters.</b>
        </p>
      </span>
      <Upload accept='.pdf,.jpg,.png' customRequest={handleUpload}
        onChange={handleFileChange} multiple={true} onRemove={handleRemoveUploaded} maxCount={5}>
        <Button disabled={isUploadButtonDisabled}>Click to Upload</Button>
      </Upload>
      {fileError && <p style={{ color: 'red' }}>{fileError}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
        <Button type='primary' onClick={onSubmitDocument} disabled={isDoneButtonDisabled || loading}>
          {loading ? <><Spin size="small" /> &nbsp; Uploading...</> : "Upload"}
        </Button>
      </div>
    </Modal>
  );
};

RegDModal.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default RegDModal;
